/**
 * Company: SPHMedia
 * Description: [Hook for Brightcove media integration]
 */

import { ClientSideSecrets } from "@app/featureFlag/featureFlags";
import mediaController from "@helper/mediaController";
import { getFeatureFlag } from "@helper/utils";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";

import { BrightcovePlayer } from "../BrightcoveVideo";

let globalMutedState = true;

type useBrightcoveMediaData = {
  accountId: string;
  playerRef: RefObject<BrightcovePlayer | null>;
  handleSuccess: (success: { ref: BrightcovePlayer }) => void;
};

type useBrightcoveMediaProps = {
  propAccountId?: string;
  onEnded?: () => void;
  nextPlaylist?: () => void;
};

/**
 * Custom React Hook: useBrightcoveMediaHook
 * Description: [Brightcove media integration hook]
 * @param props - The props required by the hook (if any)
 * @returns data - The data returned by the hook
 */
export default function useBrightcoveMedia({
  propAccountId,
  onEnded,
  nextPlaylist,
}: useBrightcoveMediaProps): useBrightcoveMediaData {
  const playerRef = useRef<BrightcovePlayer | null>(null);
  const [accountId, setAccountID] = useState<string>(propAccountId || "");
  const playerRefId = useRef(
    `brightcove-ref-${Math.random().toString(36).substr(2, 9)}`,
  );

  const fetchData = useCallback(async () => {
    try {
      const data = await getFeatureFlag(ClientSideSecrets.BC_ACCOUNT_ID);
      setAccountID(data);
    } catch (err) {
      console.error("Failed to fetch Brigtcove account ID:", err);
    }
  }, []);

  useEffect(() => {
    if (!propAccountId) {
      void fetchData();
    }
  }, [fetchData, propAccountId]);

  /**
   * @param {Object} success - The success object containing reference to the player.
   * @param {ReactPlayer} success.ref - Reference to the player.
   */
  const handleSuccess = function (success: { ref: BrightcovePlayer }) {
    const player = success.ref;

    // Apply global mute state as early as possible
    player.muted(globalMutedState);

    player.on("loadedmetadata", () => {
      player.muted(globalMutedState);

      player.on("volumechange", () => {
        globalMutedState = player.muted();
        const isPlayerPaused = player.paused();

        // If user unmutes while playing, notify other players to pause
        if (!globalMutedState && !isPlayerPaused) {
          onPlay();
        }
      });
    });

    const { onPlay, onPause } = mediaController.register({
      type: "brightcove",
      pause: () => {
        player.pause();
      },
      id: playerRefId.current,
    });

    player.on("play", () => {
      // Re-apply global mute state on play to prevent sudden sound bursts
      player.muted(globalMutedState);

      // Only pause other players if this one is actually producing sound
      if (!player.muted()) {
        onPlay();
      }
    });

    player.on("pause", () => {
      onPause();
    });

    playerRef.current = player;

    if (onEnded) {
      player.on("ended", onEnded);
    }

    const nextButton = player.el().querySelector(".vjs-next-button");
    nextButton?.addEventListener("click", () => nextPlaylist?.());
  };

  useEffect(() => {
    function handlePauseAllPlayers(e: CustomEvent<{ sourceId: string }>) {
      const player = playerRef.current;
      if (!player) return;

      // Ignore if this Brightcove is the source
      if (e.detail.sourceId === playerRefId.current) return;

      try {
        player.pause();
      } catch (err) {
        console.warn("[mediaController] Failed to pause Brightcove:", err);
      }
    }

    window.addEventListener(
      "pauseAllPlayers",
      handlePauseAllPlayers as EventListener,
    );
    return () => {
      window.removeEventListener(
        "pauseAllPlayers",
        handlePauseAllPlayers as EventListener,
      );
    };
  }, []);

  return {
    accountId,
    playerRef,
    handleSuccess,
  };
}
